/**
 * A/B Testing Performance Section
 * Provider comparison: hit rates, bias, and cost efficiency
 */

import { LoadingSpinner } from '@components/ui';
import { type DailyProviderStats, type ProviderStats, useABTestStats } from '@hooks/useABTestStats';
import { type ReactElement, useState } from 'react';

function formatNumber(num: number | null): string {
  if (num === null) return 'N/A';
  return num.toLocaleString();
}

export function ABTestingSection(): ReactElement {
  const {
    stats: abStats,
    loading: abLoading,
    error: abError,
    days: abDays,
    setDays: setAbDays,
    refetch: refetchAB,
  } = useABTestStats();
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);

  return (
    <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">A/B Testing Performance</h2>
          <p className="text-sm text-theme-text-tertiary">
            Provider comparison: hit rates, bias, and cost efficiency
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={abDays}
            onChange={(e) => setAbDays(Number(e.target.value))}
            className="px-3 py-1.5 bg-theme-bg border border-theme-border rounded text-sm text-theme-text"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-theme-text-secondary">
            <input
              type="checkbox"
              checked={showDailyBreakdown}
              onChange={(e) => setShowDailyBreakdown(e.target.checked)}
              className="rounded border-theme-border text-theme-primary focus:ring-theme-primary"
            />
            Daily view
          </label>
          <button
            type="button"
            onClick={() => refetchAB()}
            disabled={abLoading}
            className="text-sm text-theme-text-secondary hover:text-theme-text"
          >
            {abLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {abError && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4">
          <p className="text-sm text-theme-danger">{abError}</p>
        </div>
      )}

      {abLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : abStats && !showDailyBreakdown ? (
        <>
          {abStats.providers.length === 0 ? (
            <div className="text-center py-8 text-theme-text-tertiary">
              No provider data found. Generate picks to see stats.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border text-left">
                    <th className="py-2 px-2">Provider</th>
                    <th className="py-2 px-2 text-right">Gens</th>
                    <th className="py-2 px-2 text-right">Avg Cost</th>
                    <th className="py-2 px-2 text-right">Total Cost</th>
                    <th className="py-2 px-2 text-right">Over %</th>
                    <th className="py-2 px-2 text-right">Leg Hit Rate</th>
                    <th className="py-2 px-2 text-right">Parlay Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {abStats.providers.map((provider: ProviderStats) => (
                    <tr key={provider.provider} className="border-b border-theme-border/50">
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-theme-text capitalize">
                            {provider.provider}
                          </span>
                          {provider.hasBiasWarning && (
                            <span
                              className="text-theme-warning"
                              title="Over/Under bias outside 40-60% range"
                            >
                              ⚠️
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {formatNumber(provider.generations)}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        ${provider.avgCostPerGen.toFixed(4)}
                      </td>
                      <td className="py-2 px-2 text-right font-medium text-theme-success">
                        ${provider.totalCost.toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-right ${
                          provider.overPercent !== null && provider.hasBiasWarning
                            ? 'text-theme-warning font-medium'
                            : 'text-theme-text-secondary'
                        }`}
                      >
                        {provider.overPercent !== null
                          ? `${provider.overPercent.toFixed(1)}%`
                          : 'N/A'}
                      </td>
                      <td
                        className={`py-2 px-2 text-right ${
                          provider.legHitRate !== null && provider.legHitRate >= 50
                            ? 'text-theme-success font-medium'
                            : 'text-theme-text-secondary'
                        }`}
                      >
                        {provider.legHitRate !== null
                          ? `${provider.legHitRate.toFixed(1)}%`
                          : 'N/A'}
                        {provider.settledLegs > 0 && (
                          <span className="text-xs text-theme-text-tertiary ml-1">
                            ({provider.settledLegs})
                          </span>
                        )}
                      </td>
                      <td
                        className={`py-2 px-2 text-right ${
                          provider.parlayWinRate !== null && provider.parlayWinRate >= 15
                            ? 'text-theme-success font-medium'
                            : 'text-theme-text-secondary'
                        }`}
                      >
                        {provider.parlayWinRate !== null
                          ? `${provider.parlayWinRate.toFixed(1)}%`
                          : 'N/A'}
                        {provider.settledParlays > 0 && (
                          <span className="text-xs text-theme-text-tertiary ml-1">
                            ({provider.settledParlays})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="text-xs text-theme-text-tertiary space-y-1 pt-2 border-t border-theme-border">
            <p className="font-medium text-theme-text-secondary">Quality Thresholds:</p>
            <p>
              Over %: 45-55% balanced | Leg Hit: &gt;50% | Parlay Win: &gt;15% | Cost: &lt;$0.05/gen
            </p>
          </div>
        </>
      ) : abStats && showDailyBreakdown ? (
        abStats.daily.length === 0 ? (
          <div className="text-center py-8 text-theme-text-tertiary">No daily data found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-theme-border text-left">
                  <th className="py-2 px-2">Date</th>
                  <th className="py-2 px-2">Provider</th>
                  <th className="py-2 px-2 text-right">Gens</th>
                  <th className="py-2 px-2 text-right">Avg Cost</th>
                  <th className="py-2 px-2 text-right">Over %</th>
                  <th className="py-2 px-2 text-right">Leg Hits</th>
                  <th className="py-2 px-2 text-right">Parlays W/L</th>
                </tr>
              </thead>
              <tbody>
                {abStats.daily.map((row: DailyProviderStats, idx: number) => {
                  const overBiased =
                    row.overPercent !== null && (row.overPercent > 60 || row.overPercent < 40);
                  return (
                    <tr
                      key={`${row.date}-${row.provider}-${idx}`}
                      className="border-b border-theme-border/50"
                    >
                      <td className="py-2 px-2 text-theme-text-secondary">{row.date}</td>
                      <td className="py-2 px-2 font-medium text-theme-text capitalize">
                        {row.provider}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {row.generations}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        ${row.avgCost.toFixed(4)}
                      </td>
                      <td
                        className={`py-2 px-2 text-right ${overBiased ? 'text-theme-warning' : 'text-theme-text-secondary'}`}
                      >
                        {row.overPercent !== null ? `${row.overPercent.toFixed(0)}%` : 'N/A'}
                        {overBiased && ' ⚠️'}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {row.legTotal > 0 ? `${row.legHits}/${row.legTotal}` : 'N/A'}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {row.parlaysWon > 0 || row.parlaysLost > 0
                          ? `${row.parlaysWon}W/${row.parlaysLost}L`
                          : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
